import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Factory that builds a standard list+CRUD slice for a resource whose service exposes
// list/create/update/remove. Removes the per-module redux boilerplate.
//
// State: { items, total, page, limit, loading, error, saving }
// Thunks: <name>/fetchList, /createItem, /updateItem, /deleteItem
export function createCrudSlice(name, service) {
  const fetchList = createAsyncThunk(`${name}/fetchList`, async (params, { rejectWithValue }) => {
    try {
      const res = await service.list(params);
      return { res, params };
    } catch (err) {
      return rejectWithValue(err);
    }
  });

  const createItem = createAsyncThunk(`${name}/createItem`, async (payload, { rejectWithValue }) => {
    try {
      return await service.create(payload);
    } catch (err) {
      return rejectWithValue(err);
    }
  });

  const updateItem = createAsyncThunk(`${name}/updateItem`, async ({ id, payload }, { rejectWithValue }) => {
    try {
      return await service.update(id, payload);
    } catch (err) {
      return rejectWithValue(err);
    }
  });

  const deleteItem = createAsyncThunk(`${name}/deleteItem`, async (id, { rejectWithValue }) => {
    try {
      await service.remove(id);
      return id;
    } catch (err) {
      return rejectWithValue(err);
    }
  });

  const slice = createSlice({
    name,
    initialState: {
      items: [],
      total: 0,
      page: 1,
      limit: 20,
      loading: false,
      saving: false,
      error: null,
      listRequestId: null,
    },
    reducers: {
      resetError(state) {
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchList.pending, (state, { meta }) => {
          state.loading = true;
          state.error = null;
          // Remember the in-flight request so a stale, slower response can't overwrite a newer one
          // (e.g. a slow `search=x` response landing after the user already cleared the search).
          state.listRequestId = meta.requestId;
        })
        .addCase(fetchList.fulfilled, (state, { payload, meta }) => {
          if (meta.requestId !== state.listRequestId) return; // superseded by a newer fetch
          state.loading = false;
          const { res, params } = payload;
          state.items = res.data || [];
          state.total = res.pagination?.total ?? res.total ?? (res.data?.length || 0);
          state.page = res.pagination?.page ?? params?.page ?? 1;
          state.limit = res.pagination?.limit ?? params?.limit ?? state.limit;
        })
        .addCase(fetchList.rejected, (state, { payload, error, meta }) => {
          if (meta.requestId !== state.listRequestId) return; // superseded by a newer fetch
          state.loading = false;
          state.error = payload || { message: error.message };
        })
        .addMatcher(
          (a) => [createItem.pending.type, updateItem.pending.type, deleteItem.pending.type].includes(a.type),
          (state) => {
            state.saving = true;
            state.error = null;
          },
        )
        .addMatcher(
          (a) => [createItem.fulfilled.type, updateItem.fulfilled.type, deleteItem.fulfilled.type].includes(a.type),
          (state) => {
            state.saving = false;
          },
        )
        .addMatcher(
          (a) => [createItem.rejected.type, updateItem.rejected.type, deleteItem.rejected.type].includes(a.type),
          (state, { payload, error }) => {
            state.saving = false;
            state.error = payload || { message: error?.message };
          },
        );
    },
  });

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    thunks: { fetchList, createItem, updateItem, deleteItem },
  };
}
